import { EModalSize, EPredefinedModalIds } from 'src/constants/modals';
import React, { useMemo, useState } from 'react';
import {
  selectActiveConfigurationJobUuid,
  selectActiveConfigurationUuid,
  selectSelectedAssetUuid,
  selectSettingsData,
  selectSimulationProgress,
  selectSimulationResults,
  selectSimulationStatus,
} from 'src/redux/configuration/configuration.selectors';

import { EDomIds } from 'src/constants/domSelectors';
import { SimulationProgressModalExpanded } from 'src/components/SimulationProgressModal/components/SimulationProgressModalExpanded';
import { SimulationProgressModalSmall } from 'src/components/SimulationProgressModal/components/SimulationProgressModalSmall';
import { TIconNames } from 'src/components/BaseIcon/IconNames.types';
import { TSimulationProgressModalProps } from './SimulationProgressModal.types';
import classnames from 'classnames';
import { formatter } from 'src/utils/formatter';
import { meanBy } from 'lodash';
import s from './SimulationProgressModal.module.scss';
import { selectIsModalOpened } from 'src/redux/modals/modals.selectors';
import { selectSidebarExpanded } from 'src/redux/application/application.selectors';
import { useHouseSummaryResults } from 'src/hooks/useHouseSummaryResults';
import { useSelector } from 'react-redux';
import { useSimulationButtons } from 'src/hooks/useSimulationButtons';

export type TSimulationProgressModalDetailsData = {
  title: string;
  value: string;
  unit: string;
  icon: TIconNames;
}[];

export const SimulationProgressModal: React.FC<TSimulationProgressModalProps> = () => {
  const simulationProgress = useSelector(selectSimulationProgress);
  const simulationStatus = useSelector(selectSimulationStatus);
  const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
  const activeConfigurationJobUuid = useSelector(selectActiveConfigurationJobUuid);
  const selectedAssetUuid = useSelector(selectSelectedAssetUuid);
  const simulationResults = useSelector(selectSimulationResults);
  const sidebarExpanded = useSelector(selectSidebarExpanded);
  const sidebarOpened = useSelector(selectIsModalOpened(EPredefinedModalIds.MODAL_MAP_SIDEBAR));
  const summaryResults = useHouseSummaryResults({ assetUuid: selectedAssetUuid });

  const [expanded, setExpanded] = useState(false);
  const { runButtonOptions, stopButtonOptions, runButtonState } = useSimulationButtons({
    jobUuid: activeConfigurationJobUuid,
    configurationUuid: activeConfigurationUuid,
  });
  const { currency } = useSelector(selectSettingsData);

  const detailsData: TSimulationProgressModalDetailsData = useMemo(() => {
    const lastMarketSummaryItem =
      simulationResults?.marketSummary &&
      simulationResults?.marketSummary[simulationResults?.marketSummary.length - 1];

    return [
      {
        title: 'Price',
        value: formatter.formatCents(
          formatter.toOptionalFixed(
            meanBy(summaryResults.pricesData, (p) => p.y as number) || 0,
            2,
          ),
        ),
        unit: `${formatter.getCurrencySymbol(currency)} / kWh`,
        icon: 'stock-bolt',
      },
      {
        title: 'Volume',
        value: `${formatter.toOptionalFixed(lastMarketSummaryItem?.tradedVolume || 0)}`,
        unit: 'kWh',
        icon: 'volume',
      },
    ];
  }, [currency, simulationResults?.marketSummary, summaryResults.pricesData]);

  const simulationStatusTitle = useMemo(() => {
    switch (simulationStatus) {
      case 'stopped':
        return 'Simulation Stopped';
      case 'finished':
        return 'Simulation Finished';
      case 'failed':
        return 'Simulation Failed';
      case 'timed-out':
        return 'Simulation Timed-Out';
      case 'queued':
        return 'Simulation Queued';
      case 'running':
        return 'Simulation Running';
      case 'started':
        return 'Simulation Started';
      case 'initializing':
        return 'Initializing Simulation';
      case 'paused':
        return 'Simulation Paused';
      default:
        return 'Simulation Loading';
    }
  }, [simulationStatus]);

  return (
    <div
      style={{
        ['--sidebarWidth' as string]: (sidebarExpanded ? EModalSize.XL795 : EModalSize.L370) + 'px',
      }}
      className={classnames(s.container, {
        [s.expanded]: expanded,
        [s.expandedFull]: expanded && !sidebarOpened,
        [s.expandedMedium]: expanded && !sidebarExpanded && sidebarOpened,
        [s.expandedSmall]: expanded && sidebarExpanded && sidebarOpened,
      })}
      id={EDomIds.SIMULATION_PROGRESS_MODAL}>
      {!expanded && (
        <SimulationProgressModalSmall
          simulationStatusTitle={simulationStatusTitle}
          simulationProgress={simulationProgress}
          runButtonState={runButtonState}
          runButtonOptions={runButtonOptions}
          stopButtonOptions={stopButtonOptions}
          detailsData={detailsData}
          toggleExpanded={() => setExpanded(!expanded)}
        />
      )}
      {expanded && (
        <SimulationProgressModalExpanded
          simulationStatusTitle={simulationStatusTitle}
          simulationProgress={simulationProgress}
          runButtonState={runButtonState}
          runButtonOptions={runButtonOptions}
          stopButtonOptions={stopButtonOptions}
          detailsData={detailsData}
          toggleExpanded={() => setExpanded(!expanded)}
        />
      )}
    </div>
  );
};
